import React, { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Typography, Button, Box } from '@mui/material';
import { useDataProvider, useNotify, useRefresh, useRedirect } from 'react-admin';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  dialog: {
    padding: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  warning: {
    backgroundColor: '#FFF3E0',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '16px 24px',
  },
  clientInfo: {
    marginBottom: '16px',
  },
  email: {
    fontWeight: '600',
    color: '#003E6F',
  },
}));

const MergeClientsDialog = ({ open, setOpen, sourceClient, targetClient, onSuccess }) => {
  const { classes } = useStyles();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(false);

  const handleMerge = async () => {
    setLoading(true);
    try {
      await dataProvider.create('clients/merge', {
        data: {
          sourceClientId: sourceClient.id,
          targetClientId: targetClient.id,
        }
      });

      notify(`Successfully merged ${sourceClient.email} into ${targetClient.email}. Refreshing client list...`, {
        type: 'success'
      });

      setOpen(false);

      // Force a full page refresh to clear all caches
      // This ensures the merged client shows updated surveys
      setTimeout(() => {
        window.location.reload();
      }, 500);

      if (onSuccess) onSuccess();

    } catch (error) {
      const errorMessage = error?.body?.message || error.message || 'Failed to merge clients';
      notify(errorMessage, {
        type: 'error',
        multiLine: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!sourceClient || !targetClient) {
    return null;
  }

  return (
    <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
      <DialogContent className={classes.dialog}>
        <Typography className={classes.title}>
          Merge Client Profiles
        </Typography>

        <Box className={classes.warning}>
          <Typography variant="body2" fontWeight="600">
            ⚠️ Warning: This action cannot be undone
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Would you like to merge assessments from{' '}
          <span className={classes.email}>{sourceClient?.email}</span> into{' '}
          <span className={classes.email}>{targetClient?.email}</span>?
        </Typography>

        <Box className={classes.clientInfo}>
          <Typography variant="body2" color="text.secondary">
            • <strong>{targetClient?.email}</strong> will continue to exist with all assessments
            <br />
            • <strong>{sourceClient?.email}</strong> will be permanently removed from the system
            <br />
            • All assessments from <strong>{sourceClient?.email}</strong> will be transferred
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.buttons}>
        <Button
          onClick={() => setOpen(false)}
          disabled={loading}
          variant="text"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleMerge}
          disabled={loading}
        >
          {loading ? 'Merging...' : 'Confirm Merge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeClientsDialog;
